import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { SearchIcon, VideoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  apiAdminvideoNoticesGetCollection,
  getApiAdminvideoNoticesGetCollectionQueryKey,
  useApiAdminvideoNoticesIdDelete
} from '@r/api/generated/client.ts';
import type { VideoNoticeVideoNoticeCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import { DataTablePagination } from '@r/components/data-table/data-table-pagination.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import type { DataTableFilterField } from '@r/types/index.ts';
import { VideoNoticeDialog } from '@admin/components/forms/video-notices/video-notice-dialog.tsx';
import { Input } from '@ui/input.tsx';
import { Skeleton } from '@ui/skeleton.tsx';

import { VideoNoticeCard } from './video-notice-card.tsx';
import { VideoNoticeViewerDialog } from './video-notice-viewer-dialog.tsx';

const paginationColumns: ColumnDef<VideoNoticeVideoNoticeCollectionReadModel>[] = [
  { accessorKey: 'id' }
];
const noFilterFields: DataTableFilterField<VideoNoticeVideoNoticeCollectionReadModel>[] = [];

/**
 * Récupère toutes les pages de notices par lots de 30 afin de permettre une recherche globale,
 * l’API ne proposant pas encore de paramètre de recherche.
 */
async function getAllVideoNotices() {
  const firstPage = await apiAdminvideoNoticesGetCollection({ page: 1, perPage: 30 });
  const lastPage = firstPage.pagination?.last ?? 1;
  const remainingPages =
    lastPage > 1
      ? await Promise.all(
          Array.from({ length: lastPage - 1 }, (_, index) =>
            apiAdminvideoNoticesGetCollection({ page: index + 2, perPage: 30 })
          )
        )
      : [];

  return [...(firstPage.data ?? []), ...remainingPages.flatMap((page) => page.data ?? [])];
}

interface VideoNoticesTableProps {
  canManage?: boolean;
  showSearch?: boolean;
  listTitle?: string;
}

/**
 * Charge, filtre et pagine les notices vidéo.
 * Le mode gestion active l’édition et la suppression, sinon un clic ouvre le lecteur.
 */
export function VideoNoticesTable({
  canManage = true,
  showSearch = false,
  listTitle
}: VideoNoticesTableProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showOrvalErrorToast = useOrvalErrorToast();
  const [search, setSearch] = useState('');
  const [videoNoticeToEdit, setVideoNoticeToEdit] =
    useState<VideoNoticeVideoNoticeCollectionReadModel | null>(null);
  const [videoNoticeToView, setVideoNoticeToView] =
    useState<VideoNoticeVideoNoticeCollectionReadModel | null>(null);

  const deleteVideoNotice = useApiAdminvideoNoticesIdDelete({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: getApiAdminvideoNoticesGetCollectionQueryKey()
        });
        toast.success(t('fiddy.admin.video.notices.delete.success'));
      },
      onError: (error) => {
        showOrvalErrorToast(error, t('fiddy.admin.video.notices.delete.error'));
      }
    }
  });

  /**
   * Sélectionne une notice et ouvre la modale d’édition associée.
   */
  const handleEdit = (videoNotice: VideoNoticeVideoNoticeCollectionReadModel) => {
    setVideoNoticeToEdit(videoNotice);
  };

  /**
   * Demande confirmation avant de déclencher la suppression côté API.
   */
  const handleDelete = (videoNotice: VideoNoticeVideoNoticeCollectionReadModel) => {
    toast(t('fiddy.admin.video.notices.delete.confirm', { title: videoNotice.title }), {
      action: {
        label: t('fiddy.admin.video.notices.delete.confirm.action'),
        onClick: () => deleteVideoNotice.mutate({ id: String(videoNotice.id) })
      },
      cancel: {
        label: t('fiddy.admin.video.notices.delete.cancel.action'),
        onClick: () => {}
      }
    });
  };

  const query = useQuery({
    queryKey: [...getApiAdminvideoNoticesGetCollectionQueryKey(), 'all'],
    queryFn: getAllVideoNotices
  });

  // Applique la recherche sur l’ensemble des données chargées, et non sur la page visible.
  const filteredVideoNotices = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase();
    if (!normalizedSearch) return query.data ?? [];

    return (query.data ?? []).filter((videoNotice) =>
      [videoNotice.title, videoNotice.category, videoNotice.description].some((value) =>
        value?.toLocaleLowerCase().includes(normalizedSearch)
      )
    );
  }, [query.data, search]);

  const { table } = useDataTable({
    data: filteredVideoNotices,
    columns: paginationColumns,
    filterFields: noFilterFields,
    manualPaginate: false
  });

  useOrvalQueryErrorToast(
    {
      isError: query.isError,
      error: query.error,
      errorUpdatedAt: query.errorUpdatedAt
    },
    t(canManage ? 'fiddy.admin.video.notices.list.error' : 'admin.video.notices.list.error')
  );

  if (query.isLoading) {
    return (
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="overflow-hidden rounded-xl border bg-card">
            <Skeleton className="aspect-video w-full rounded-none" />
            <div className="space-y-3 p-4">
              <Skeleton className="h-5 w-4/5" />
              <div className="flex justify-between">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const paginatedVideoNotices = table.getRowModel().rows.map((row) => row.original);

  return (
    <>
      {showSearch && (
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 z-10 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              table.setPageIndex(0);
            }}
            className="pl-9"
            placeholder={t('admin.video.notices.search.placeholder')}
          />
        </div>
      )}

      {listTitle && <h2 className="text-2xl font-bold">{listTitle}</h2>}

      {paginatedVideoNotices.length > 0 ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {paginatedVideoNotices.map((videoNotice) => (
            <VideoNoticeCard
              key={videoNotice.id}
              videoNotice={videoNotice}
              onEdit={canManage ? handleEdit : undefined}
              onDelete={canManage ? handleDelete : undefined}
              onOpen={!canManage ? setVideoNoticeToView : undefined}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center gap-2 rounded-xl border py-12 text-muted-foreground">
          <VideoIcon className="size-12" />
          <p className="font-medium">
            {t(
              canManage ? 'fiddy.admin.video.notices.list.empty' : 'admin.video.notices.list.empty'
            )}
          </p>
        </div>
      )}

      <DataTablePagination
        table={table}
        totalItems={filteredVideoNotices.length}
        pageSizeOptions={[10, 20, 30]}
      />

      {!canManage && (
        <VideoNoticeViewerDialog
          videoNotice={videoNoticeToView}
          onOpenChange={(open) => {
            if (!open) setVideoNoticeToView(null);
          }}
        />
      )}

      {canManage && (
        <VideoNoticeDialog
          isOpen={videoNoticeToEdit !== null}
          videoNotice={videoNoticeToEdit}
          onOpenChange={(open) => {
            if (!open) setVideoNoticeToEdit(null);
          }}
        />
      )}
    </>
  );
}
